import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from './useApi';

const useSupport = () => {
  const { isAuthenticated } = useAuth();
  const { apiCall } = useApi();
  const [tickets, setTickets] = useState([]);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Récupérer les tickets avec filtres et pagination
  const fetchTickets = useCallback(async (page = 1, filters = {}) => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });

      // Ajouter les filtres
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const response = await apiCall(`/admin/support/tickets?${params}`);
      
      if (response) {
        setTickets(response.tickets || []);
        setPagination(response.pagination || pagination);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des tickets:', err);
      setError('Erreur lors du chargement des tickets');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall, pagination.limit]);

  // Récupérer un ticket spécifique avec ses messages
  const fetchTicket = useCallback(async (ticketId) => {
    if (!isAuthenticated || !ticketId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiCall(`/admin/support/tickets/${ticketId}`);
      setCurrentTicket(response);
      
      return response;
    } catch (err) {
      console.error('Erreur lors de la récupération du ticket:', err);
      setError('Erreur lors du chargement du ticket');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall]);

  // Mettre à jour un ticket
  const updateTicket = useCallback(async (ticketId, updates) => {
    try {
      const response = await apiCall(`/admin/support/tickets/${ticketId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      
      // Mettre à jour le ticket courant si c'est le même
      if (currentTicket && currentTicket.id === ticketId) {
        setCurrentTicket(prevTicket => ({ ...prevTicket, ...response }));
      }
      
      // Mettre à jour la liste des tickets
      setTickets(prevTickets =>
        prevTickets.map(ticket =>
          ticket.id === ticketId ? { ...ticket, ...response } : ticket
        )
      );
      
      return response;
    } catch (err) {
      console.error('Erreur lors de la mise à jour du ticket:', err);
      throw err;
    }
  }, [apiCall, currentTicket]);

  // Ajouter un message à un ticket
  const addMessage = useCallback(async (ticketId, messageData) => {
    try {
      const response = await apiCall(`/admin/support/tickets/${ticketId}/messages`, {
        method: 'POST',
        body: JSON.stringify(messageData)
      });
      
      // Ajouter le message au ticket courant si c'est le même
      if (currentTicket && currentTicket.id === ticketId) {
        setCurrentTicket(prevTicket => ({
          ...prevTicket,
          messages: [...(prevTicket.messages || []), response]
        }));
      }
      
      return response;
    } catch (err) {
      console.error('Erreur lors de l\'ajout du message:', err);
      throw err;
    }
  }, [apiCall, currentTicket]);

  // Récupérer les statistiques du support
  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiCall('/admin/support/stats');
      setStats(response);
      return response;
    } catch (err) {
      console.error('Erreur lors de la récupération des statistiques:', err);
      setError('Erreur lors du chargement des statistiques');
    }
  }, [isAuthenticated, apiCall]);

  // Récupérer les catégories
  const fetchCategories = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiCall('/admin/support/categories');
      setCategories(response);
      return response;
    } catch (err) {
      console.error('Erreur lors de la récupération des catégories:', err);
      setError('Erreur lors du chargement des catégories');
    }
  }, [isAuthenticated, apiCall]);

  // Récupérer les agents
  const fetchAgents = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiCall('/admin/support/agents');
      setAgents(response);
      return response;
    } catch (err) {
      console.error('Erreur lors de la récupération des agents:', err);
      setError('Erreur lors du chargement des agents');
    }
  }, [isAuthenticated, apiCall]);

  // Rafraîchir toutes les données
  const refresh = useCallback((filters = {}) => {
    fetchTickets(pagination.page, filters);
    fetchStats();
    fetchCategories();
    fetchAgents();
  }, [fetchTickets, fetchStats, fetchCategories, fetchAgents, pagination.page]);

  // Charger les données initiales
  useEffect(() => {
    if (isAuthenticated) {
      fetchTickets(1, {});
      fetchStats();
      fetchCategories();
      fetchAgents();
    }
  }, [isAuthenticated, fetchTickets, fetchStats, fetchCategories, fetchAgents]);

  return {
    // Data
    tickets,
    currentTicket,
    stats,
    categories,
    agents,
    loading,
    error,
    pagination,
    
    // Actions
    fetchTickets,
    fetchTicket,
    updateTicket,
    addMessage,
    refresh,
    
    // Utils
    setCurrentTicket,
    isAuthenticated
  };
};

export default useSupport;